/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import Table from "components/Table";
import { clubTableColumns } from "utils/tableHelper/playerCardTableHelper";
import ContentShell from "components/ContentShell";
import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import configData from "config/config.json";
import { getClubs } from "services/playerCardService";
import usePaginationHook from "../../../hooks/usePagination";
import { JSONSearch, opacity2hex, sortArray } from "utils/miscUtils";
import SearchInput from "components/SearchInput";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import { useSnackbar } from "hooks/useSnackbar";
import CustomButton from "components/CustomButton";
import EditIcon from "@mui/icons-material/Edit";

const useStyles = makeStyles((theme) => ({
  savecancel: {
    width: "160px",
    height: 40,
  },
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  playerCardParent: {
    padding: "20px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  searchInput: {
    width: "270px",
    marginBottom: 20,
  },
}));

const RowActions = ({ rowId, data }: any) => {
  const navigate = useNavigate();
  const handleRowAction = (data: any) =>
    navigate(
      configData.routes.playerDetails.editClub.replace(":id", data?._id)
    );

  return (
    <CustomButton
      onClick={() => handleRowAction(data)}
      startIcon={<EditIcon />}
    >
      Edit
    </CustomButton>
  );
};

const ClubsList = () => {
  const navigate = useNavigate();
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const [orderDirection, setOrderDirection] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [fullList, setFullList] = useState([]); // This is your original unfiltered list
  const [searchValue, setSearchValue] = useState("");

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.CLUB.UPDATE_ROLES
  );

  const { pageNo, pageSize } = usePaginationHook({
    totalItems: filteredList.length,
  });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const fetchData = async (v) => {
    try {
      setLoading(true);
      const response = await getClubs({
        pageNo,
        pageSize: pageSize,
        search: v,
      });
      const data = response.data.map((item) => {
        const cleanedItem = Object.keys(item).reduce((acc, key) => {
          if (key === "isJerseyUpload") {
            acc[key] = item[key] ? "Yes" : "No";
          } else {
            acc[key] = item[key] ?? "-";
          }
          return acc;
        }, {});
        return cleanedItem;
      });

      setFullList(data);
      setFilteredList(data);
      setLoading(false);
    } catch (error) {
      showSnackbar(error.message);
      setLoading(false);
      console.error("Error fetching club data: ", error);
    }
  };

  const onSearch = (value: any) => {
    setSearchValue(value);

    if (value === "") {
      setFilteredList(fullList);
    }

    setFilteredList(JSONSearch(fullList, ["name", "abbreviation"], value));
  };

  const handleCreateClub = () => {
    navigate(configData.routes.playerDetails.createClub);
  };

  useEffect(() => {
    const initialColumns = clubTableColumns().filter(
      (col) => col.id !== "actions" || hasEditPermission
    );
    setColumns(initialColumns);

    fetchData(searchValue);
  }, [pageNo, pageSize, hasEditPermission]);

  return (
    <ContentShell
      loading={loading}
      title="Clubs"
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a club"
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.CLUB.CREATE_ROLES) && (
            <CustomButton onClick={handleCreateClub}>Create</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <Table
          columns={columns}
          data={filteredList}
          actions={RowActions}
          onColumnClick={onColumnClick}
          totalEntries={filteredList.length}
          highlightAltRow={true}
        />
      </Box>
    </ContentShell>
  );
};

export default ClubsList;
